import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { tone as toneMap, type Tone } from '../tokens';

/**
 * Rectangular label, radius 4–6. Use it for a classification, not a status —
 * a status belongs in a `StatusPill` (radius 20).
 *
 * - `mono` (radius 4, JetBrains Mono 10px) — service badge "NTA 8060", rail counters
 * - `text` (radius 6, DM Sans 11px)        — "Foto verplicht"
 */
export type BadgeVariant = 'mono' | 'text';

export type BadgeProps = Omit<ComponentPropsWithRef<'span'>, 'children'> & {
    variant?: BadgeVariant;
    tone?: Tone;
    /** 500 for the alarm state of "Foto verplicht"; 400 otherwise. */
    weight?: 'regular' | 'medium';
    children: ReactNode;
};

const variantStyles: Record<BadgeVariant, string> = {
    mono: 'font-[family-name:var(--font-mono)] text-[10px] tracking-[0.06em] px-[6px] py-[3px] rounded-[4px]',
    text: 'font-[family-name:var(--font-body)] text-[11px] px-[9px] py-[6px] rounded-[6px]',
};

export function Badge({
    variant = 'mono',
    tone = 'neutral',
    weight = 'regular',
    className,
    children,
    ...rest
}: BadgeProps) {
    const pair = toneMap[tone];

    return (
        <span
            className={cn(
                'inline-flex items-center whitespace-nowrap leading-[1.3]',
                weight === 'medium' ? 'font-medium' : 'font-normal',
                variantStyles[variant],
                className,
            )}
            style={{ background: pair.bg, color: pair.fg }}
            {...rest}
        >
            {children}
        </span>
    );
}

/**
 * The blue "why is this question here" label from the opname screen.
 * Plain language, never norm-speak: "Verschijnt omdat hoofddak = pannen".
 *
 * It is a pill shape but it is not a status, so it lives here rather than in
 * `StatusPill`. Keep it next to the question it explains — conditional logic
 * without an explanation reads as a bug (principle 2).
 */
export type WhyLabelProps = Omit<ComponentPropsWithRef<'span'>, 'children'> & {
    children: ReactNode;
};

export function WhyLabel({ className, children, ...rest }: WhyLabelProps) {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-[7px] rounded-[20px] px-[10px] py-1',
                'bg-[var(--info-tint)] text-[var(--info-text)]',
                'font-[family-name:var(--font-body)] text-[11px] leading-[1.35]',
                className,
            )}
            {...rest}
        >
            <span aria-hidden="true" className="h-[6px] w-[6px] shrink-0 rounded-full bg-current" />
            {children}
        </span>
    );
}
